import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Chip,
  Button,
  TextField,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  AdminPanelSettings,
  Lock,
  AccountCircle,
  Payment,
  Warning,
  Event,
  TrendingUp,
  Security,
  CheckCircle,
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { formatINR } from '../utils/currency';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import { StatusIndicator, statusAccent } from '../components/Indicators';
import { ACCENTS, tint } from '../theme/accents';

const Profile = () => {
  const { user, isAdmin, changePassword, updateProfile } = useAuth();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [editForm, setEditForm] = useState({
    fullName: '',
    email: '',
    phone: '',
  });

  useEffect(() => {
    fetchProfile();
    fetchStats();
    fetchRecentActivity();
  }, [user]);

  const fetchProfile = async () => {
    if (!user) return;
    try {
      const response = await axios.get(`/api/users/${user.id}`);
      setProfile(response.data);
      setEditForm({
        fullName: response.data.full_name,
        email: response.data.email || '',
        phone: response.data.phone || '',
      });
    } catch (error) {
      console.error('Failed to fetch profile');
    }
  };

  const fetchStats = async () => {
    if (!user) return;
    try {
      const currentMonth = dayjs().format('YYYY-MM');
      const [paymentsRes, penaltiesRes] = await Promise.all([
        axios.get(`/api/payments/user/${user.id}`),
        axios.get(`/api/penalties/user/${user.id}`),
      ]);

      const totalPaymentAmount = paymentsRes.data.reduce((sum, p) => sum + parseFloat(p.amount), 0);
      const completedPayments = paymentsRes.data.filter(p => p.status === 'completed').length;
      const currentMonthPayment = paymentsRes.data.find(p => p.payment_month === currentMonth);
      
      const totalPenaltyAmount = penaltiesRes.data.reduce((sum, p) => sum + parseFloat(p.amount), 0);
      const pendingPenalties = penaltiesRes.data.filter(p => p.status === 'pending');
      const paidPenalties = penaltiesRes.data.filter(p => p.status === 'paid').length;

      setStats({
        totalPaymentAmount,
        completedPayments,
        totalPayments: paymentsRes.data.length,
        currentMonthStatus: currentMonthPayment?.status || 'pending',
        totalPenaltyAmount,
        pendingPenaltiesCount: pendingPenalties.length,
        pendingPenaltiesAmount: pendingPenalties.reduce((sum, p) => sum + parseFloat(p.amount), 0),
        paidPenalties,
      });
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const fetchRecentActivity = async () => {
    if (!user) return;
    try {
      const [paymentsRes, penaltiesRes] = await Promise.all([
        axios.get(`/api/payments/user/${user.id}`),
        axios.get(`/api/penalties/user/${user.id}`),
      ]);

      const activities = [
        ...paymentsRes.data.slice(0, 3).map(p => ({
          type: 'payment',
          title: `Payment · ${dayjs(`${p.payment_month}-01`).format('MMM YYYY')}`,
          subtitle: formatINR(p.amount),
          status: p.status,
          date: p.payment_date,
          icon: <Payment />,
        })),
        ...penaltiesRes.data.slice(0, 3).map(p => ({
          type: 'penalty',
          title: p.reason,
          subtitle: formatINR(p.amount),
          status: p.status,
          date: p.penalty_date,
          icon: <Warning />,
        })),
      ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5);

      setRecentActivity(activities);
    } catch (error) {
      console.error('Failed to fetch recent activity');
    }
  };

  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (passwordForm.newPassword === passwordForm.currentPassword) {
      toast.error('New password must be different from the current password');
      return;
    }

    const result = await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
    if (result.success) {
      setPasswordDialogOpen(false);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    }
  };

  const handleProfileUpdate = async () => {
    // Saving updates `user` in AuthContext, which re-runs the effect above and
    // refreshes the profile card, so there is no separate refetch here.
    const result = await updateProfile(editForm);
    if (result.success) {
      setEditDialogOpen(false);
    }
  };

  if (!profile) {
    return <Box>Loading...</Box>;
  }

  return (
    <Box>
      <PageHeader
        eyebrow="Account"
        accent={ACCENTS.indigo}
        title="My Profile"
        subtitle="Your contact details, contribution history and account security."
      />

      <Grid container spacing={3}>
        {/* Profile Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" flexDirection="column" alignItems="center">
                <Avatar
                  sx={{
                    width: 100,
                    height: 100,
                    bgcolor: user?.isAdmin ? 'warning.main' : 'primary.main',
                    fontSize: '2.5rem',
                    mb: 2,
                  }}
                >
                  {profile.full_name.charAt(0)}
                </Avatar>
                <Typography variant="h5" gutterBottom>
                  {profile.full_name}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  @{profile.username}
                </Typography>
                {isAdmin && (
                  <Chip
                    icon={<AdminPanelSettings />}
                    label="Administrator"
                    color="warning"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <List>
                <ListItem>
                  <ListItemIcon>
                    <Email />
                  </ListItemIcon>
                  <ListItemText
                    primary="Email"
                    secondary={profile.email || 'Not provided'}
                    secondaryTypographyProps={{ sx: { overflowWrap: 'anywhere' } }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Phone />
                  </ListItemIcon>
                  <ListItemText
                    primary="Phone"
                    secondary={profile.phone || 'Not provided'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Event />
                  </ListItemIcon>
                  <ListItemText
                    primary="Member Since"
                    secondary={dayjs(profile.created_at).format('MMMM D, YYYY')}
                  />
                </ListItem>
              </List>

              <Box sx={{ mt: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Person />}
                  onClick={() => setEditDialogOpen(true)}
                  sx={{ mb: 1 }}
                >
                  Edit Profile
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color="warning"
                  startIcon={<Lock />}
                  onClick={() => setPasswordDialogOpen(true)}
                >
                  Change Password
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Stats Cards */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                      <Payment />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {formatINR(stats?.totalPaymentAmount)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Payments ({stats?.completedPayments || 0} completed)
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar 
                      sx={{ 
                        bgcolor: stats?.currentMonthStatus === 'completed' ? 'success.main' : 'warning.main',
                        mr: 2 
                      }}
                    >
                      <CheckCircle />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {dayjs().format('MMMM')} Payment
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <StatusIndicator status={stats?.currentMonthStatus || 'pending'} size="small" />
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                      <Warning />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {stats?.pendingPenaltiesCount || 0} Pending
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Penalties: {formatINR(stats?.pendingPenaltiesAmount)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                      <TrendingUp />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {stats?.paidPenalties || 0} Paid
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Penalties: {formatINR(stats?.totalPenaltyAmount)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Recent Activity */}
          <Paper sx={{ mt: 3, p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            <List>
              {recentActivity.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No recent activity
                </Typography>
              ) : (
                recentActivity.map((activity, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: (t) => tint(statusAccent(activity.status), 0.16),
                          color: statusAccent(activity.status),
                          border: '1px solid',
                          borderColor: (t) => tint(statusAccent(activity.status), 0.4),
                          '& svg': { fontSize: 17 },
                        }}
                      >
                        {activity.icon}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={activity.title}
                      secondary={`${activity.subtitle} · ${dayjs(activity.date).format('D MMM YYYY')}`}
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                    <StatusIndicator status={activity.status} size="small" />
                  </ListItem>
                ))
              )}
            </List>
          </Paper>

          {/* Security Notice */}
          <Alert severity="info" sx={{ mt: 3 }}>
            <Box display="flex" alignItems="center">
              <Security sx={{ mr: 1 }} />
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  Security Notice
                </Typography>
                <Typography variant="body2">
                  All your activities are logged and audited for security purposes.
                  {isAdmin && ' As an admin, your actions have additional audit tracking.'}
                </Typography>
              </Box>
            </Box>
          </Alert>
        </Grid>
      </Grid>

      {/* Edit Profile Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                value={editForm.fullName}
                onChange={(e) => setEditForm({ ...editForm, fullName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone (WhatsApp)"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                helperText="Used for WhatsApp notifications. Include the country code, e.g. 917984821374; a 10-digit number is saved with 91 added."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleProfileUpdate} variant="contained">
            Update Profile
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Current Password"
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                helperText="Minimum 6 characters"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Confirm New Password"
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
          <Button onClick={handlePasswordChange} variant="contained">
            Change Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile;